import { IBookingCartPreviewCartItem } from "@/api_framework/api_modals/Bookings";
import { getVaccineId } from "@/app/utils/bookingServiceSelection";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import PetVaccinationStatus from "@/app/components/ui/Reusable/PetVaccinationStatus";
import { useUtils } from "@/app/hooks/useUtils";

interface IChooseVaccineSectionProps {
  isLoading: boolean;
  petName?: string;
  vaccines: IBookingCartPreviewCartItem[];
  selectedVaccineIds: string[];
  vaccineCouponMap: Record<string, string>;
  formatAmount: (amount: number) => string;
  onToggleVaccine: (vaccineId: string) => void;
  onSelectVaccineCoupon: (vaccineId: string, couponId: string | null, couponName?: string) => void;
}

const skeletonClass = "animate-pulse rounded bg-[#E9ECF2]";


const ChooseVaccineSection: React.FC<IChooseVaccineSectionProps> = ({
  isLoading,
  petName,
  vaccines,
  selectedVaccineIds,
  vaccineCouponMap,
  formatAmount,
  onToggleVaccine,
  onSelectVaccineCoupon,
}) => {
  const selectedSet = new Set(selectedVaccineIds);
  const { veticDateFormat } = useUtils();

  return (
    <section className="px-4">
      <h4 className="mb-3 text-[14px] font-semibold text-[#222222] capitalize">
        Vaccination for {petName}
      </h4>

      <div className="rounded-[10px] bg-[#F5F4F4] p-[1px]">
        {/* header */}
        <div className="hidden grid-cols-[1.4fr_1fr_1.1fr_1.4fr_0.9fr] gap-3 rounded-t-[9px] bg-[#F5F4F4] px-4 py-3 text-[12px] font-medium text-[#222222] md:grid">
          <p>Vaccine</p>
          <p>Due Date</p>
          <p>Pricing</p>
          <p>Discount</p>
          <p>Actions</p>
        </div>

        {isLoading ? (
          <div className="m-[2px] rounded-[10px] bg-white space-y-3 p-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className={`${skeletonClass} h-[44px] w-full`} />
            ))}
          </div>
        ) : vaccines.length > 0 ? (
          vaccines.map((vaccine, index) => {
            const vaccineId = getVaccineId(vaccine);
            const isAdded = selectedSet.has(vaccineId);
            const originalPrice = Number(vaccine.price ?? 0);
            const finalPrice = Number(vaccine.final_price ?? originalPrice);
            const discountPercent =
              vaccine.discount_percent ??
              (originalPrice > 0 ? Math.max(0, Math.round(((originalPrice - finalPrice) / originalPrice) * 100)) : 0);
            const hasPricing = originalPrice > 0 || finalPrice > 0;
            const maxQty = Number(vaccine.max_qty ?? 1);
            const quantityAdded = Number(vaccine.qty ?? (isAdded ? 1 : 0)) || (isAdded ? 1 : 0);
            const isMaxQtyReached = quantityAdded >= maxQty;

            const vaccineLabel = vaccine.vaccine_name || vaccine.name || "Vaccine";

            // Per-line discount picker from applicable_coupons.
            const applicableCoupons = vaccine.applicable_coupons ?? [];
            const couponItems = applicableCoupons.map((coupon) => ({
              id: coupon.coupon_id,
              title: coupon.coupon_name,
              state: { coupon_visibility: coupon.coupon_visibility },
            }));
            const selectedCouponId = vaccineCouponMap[vaccineId] || vaccine.coupon_id || "";
            const hasCoupons = couponItems.length > 0;

            return (
              <div
                key={`${vaccineId}-${index}`}
                className="m-[2px] mb-2 last:mb-[2px] grid grid-cols-1 gap-3 rounded-[10px] bg-white px-4 py-[10px] text-[12px] text-[#232932] md:grid-cols-[1.4fr_1fr_1.1fr_1.4fr_0.9fr] md:items-center"
              >
                {/* Vaccine */}
                <div className="min-w-0">
                  <p className="font-medium text-[#2B2E34]">{vaccineLabel}</p>
                </div>

                {/* Due Date */}
                <div className="text-[12px]">
                  {vaccine.vaccination_details ? (
                    <>
                      {vaccine.vaccination_details.due_date && (
                        <p className="font-medium text-[#2B2E34]">{veticDateFormat(vaccine.vaccination_details.due_date)}</p>
                      )}
                      {vaccine.vaccination_details.status && (
                        <PetVaccinationStatus
                          status={vaccine.vaccination_details.status}
                          type="vaccination"
                          className="pointer-events-none mt-0.5 no-underline"
                        />
                      )}
                    </>
                  ) : (
                    <p className="text-[#9AA1AD] pl-4">-</p>
                  )}
                </div>

                {/* Pricing */}
                <div className="flex items-center gap-1.5 text-[12px]">
                  {hasPricing ? (
                    <>
                      <span className="font-medium text-[#2B2E34]">{formatAmount(finalPrice)}</span>
                      {discountPercent > 0 && originalPrice > finalPrice && (
                        <>
                          <span className="text-[#6F7685] line-through">{formatAmount(originalPrice)}</span>
                          <span className="font-medium text-[#239A3D]">{discountPercent}%</span>
                        </>
                      )}
                    </>
                  ) : (
                    <span className="font-medium text-[#2B2E34]">NA</span>
                  )}
                </div>

                {/* Discount (per-line coupon picker) */}
                <div className="min-w-0">
                  <SearchableSelectMenu
                    register={{} as any}
                    fieldError={undefined}
                    errorMessages={[]}
                    placeholder={hasCoupons ? "Select discount" : "No discount"}
                    showDropdownIcon={true}
                    withReset={hasCoupons}
                    disabled={!hasCoupons}
                    listZIndex="z-30"
                    listBoxClassName="min-w-full w-max max-w-[320px]"
                    defaultSelectId={selectedCouponId}
                    selectItems={couponItems}
                    className={`text-[12px] font-medium text-[#3B4250] ${!hasCoupons ? "!bg-transparent" : ""}`}
                    inputContainerClassName={`h-[36px] flex items-center border-[#E2E2E2] rounded-[8px] ${hasCoupons ? "bg-white" : "bg-gray-200"}`}
                    containerClassName="w-full"
                    onSelectItem={(item) => onSelectVaccineCoupon(vaccineId, item?.id ?? null, item?.title)}
                    filterOnEmptyQuery={(item) => item.state?.coupon_visibility !== false}
                    onEnterPress={(typedValue) => {
                      const exactMatch = applicableCoupons?.find(
                        (c) => c.coupon_name?.toLowerCase() === typedValue?.toLowerCase(),
                      );
                      if (exactMatch) {
                        if (exactMatch.coupon_id === selectedCouponId) return;
                        onSelectVaccineCoupon(vaccineId, exactMatch.coupon_id, exactMatch.coupon_name);
                      } else {
                        onSelectVaccineCoupon(vaccineId, null);
                      }
                    }}
                  />
                </div>

                {/* Actions */}
                <div className="flex md:justify-start">
                  {isAdded ? (
                    <div className="flex h-[36px] min-w-[95px] items-center justify-between rounded-[6px] border-[1.5px] border-[#000000]">
                      <button type="button" className="px-3 py-1 text-[#222222]" onClick={() => onToggleVaccine(vaccineId)}>
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      <span className="text-[12px] text-[#222222]">{quantityAdded}</span>
                      <button
                        type="button"
                        className={`px-3 py-1 ${isMaxQtyReached ? "cursor-not-allowed text-[#8F8F8F]" : "text-[#222222]"}`}
                        disabled={isMaxQtyReached}
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={!hasPricing}
                      onClick={() => onToggleVaccine(vaccineId)}
                      className={`flex h-[34px] min-w-[94px] items-center justify-center rounded-[6px] px-[17px] py-[8px] text-[12px] font-semibold ${hasPricing
                        ? "bg-[#000000] text-[#ffffff]"
                        : "cursor-not-allowed border-[#000000] bg-[#FFFFFF] text-[#000000]"
                        }`}
                    >
                      Add
                    </button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="p-4 text-[12px] text-[#6B7280] m-[2px] rounded-[10px] bg-white">No vaccines available for this pet yet.</div>
        )}
      </div>
    </section>
  );
};

export default ChooseVaccineSection;
